import type { Document, Folder } from "@models";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { DocumentStatus } from "@types";
import { useMemo, useState, type ReactNode } from "react";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { DocumentDeleteConfirmationContent } from "src/components/Documents/DocumentDeleteConfirmationContent";
import {
  useDeleteFolderMutation,
  usePatchDocumentMutation,
  usePatchFolderMutation,
} from "src/hooks/mutations";
import { useDeleteDocumentAction } from "src/hooks/useDeleteDocumentAction";
import { getLatestDocumentStatus } from "src/utils/documents";

import type { DocumentsPageItem } from "../Documents.utils";
import type {
  DocumentsTableMoveTarget,
  DocumentsTableRow,
} from "../DocumentsTable.types";
import { SetDocumentScopeDialog } from "../SetDocumentScopeDialog";
import { DocumentsTableMoveDialog } from "./DocumentsTableMoveDialog";
import {
  DocumentsTableRowActions,
  DocumentsTableRowActionsMenu,
  type DocumentsTableRowActionsProps,
} from "./DocumentsTableRowActions";

const LIVE_DOCUMENT_RENAME_TOOLTIP_TITLE = "Live documents can't be renamed.";

type UseDocumentsTableRowActionsArgs = {
  currentDeviceId: string | null;
  documentQueryDeviceIds: string[];
  documents: Document[];
  folders: Folder[];
  isLoading: boolean;
  onOpenDocumentInNewTab: (
    item: Extract<DocumentsPageItem, { kind: "document" }>
  ) => void;
  onOpenItem: (item: DocumentsPageItem) => void;
  orgId: string;
};

type UseDocumentsTableRowActionsResult = {
  openRowActionContextMenu: (
    item: DocumentsPageItem,
    anchorPosition: { top: number; left: number }
  ) => void;
  renderActionsCell: (
    params: GridRenderCellParams<DocumentsTableRow, string>,
    options?: { disabled?: boolean }
  ) => ReactNode;
  renderRowActionContextMenu: (options?: { disabled?: boolean }) => ReactNode;
  renderRowActionDialogs: (options?: { disabled?: boolean }) => ReactNode;
  isInteractionDisabled: boolean;
};

const isDocumentLive = (document: Document) =>
  getLatestDocumentStatus(document) === DocumentStatus.LIVE;

const canRenameDocumentsPageItem = (item: DocumentsPageItem) =>
  item.kind === "folder" || !isDocumentLive(item.document);

const isFolderInsideFolder = ({
  ancestorFolderId,
  folderId,
  foldersById,
}: {
  ancestorFolderId: string;
  folderId: string;
  foldersById: Map<string, Folder>;
}) => {
  let currentFolderId: string | null = folderId;

  while (currentFolderId) {
    if (currentFolderId === ancestorFolderId) {
      return true;
    }

    currentFolderId = foldersById.get(currentFolderId)?.parentFolderId ?? null;
  }

  return false;
};

export const useDocumentsTableRowActions = ({
  currentDeviceId,
  documentQueryDeviceIds,
  documents,
  folders,
  isLoading,
  onOpenDocumentInNewTab,
  onOpenItem,
  orgId,
}: UseDocumentsTableRowActionsArgs): UseDocumentsTableRowActionsResult => {
  const deleteFolderMutation = useDeleteFolderMutation();
  const patchDocumentMutation = usePatchDocumentMutation();
  const patchFolderMutation = usePatchFolderMutation();
  const {
    canDeleteDocument,
    deleteDocument,
    getDeleteDocumentTooltipTitle,
    isDeleteDocumentPending,
  } = useDeleteDocumentAction({
    deviceId: currentDeviceId ?? undefined,
    documents,
    orgId,
    queryDeviceIds: documentQueryDeviceIds,
  });
  const [renamingItem, setRenamingItem] = useState<DocumentsPageItem | null>(
    null
  );
  const [scopeDocument, setScopeDocument] = useState<Document | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [moveItem, setMoveItem] = useState<DocumentsPageItem | null>(null);
  const [pendingFolderDeleteItem, setPendingFolderDeleteItem] =
    useState<Extract<DocumentsPageItem, { kind: "folder" }> | null>(null);
  const [pendingDocumentDeleteItem, setPendingDocumentDeleteItem] =
    useState<Extract<DocumentsPageItem, { kind: "document" }> | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    anchorPosition: { top: number; left: number };
    item: DocumentsPageItem;
  } | null>(null);
  const foldersById = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder])),
    [folders]
  );
  const isInteractionDisabled =
    isLoading ||
    isDeleteDocumentPending ||
    deleteFolderMutation.isPending ||
    patchDocumentMutation.isPending ||
    patchFolderMutation.isPending;

  const resetRenameState = () => {
    setRenamingItem(null);
    setRenameDraft("");
  };

  const handleRenameStart = (item: DocumentsPageItem) => {
    if (isInteractionDisabled || !canRenameDocumentsPageItem(item)) {
      return;
    }

    setRenamingItem(item);
    setRenameDraft(item.name.trim() || "No name");
  };

  const handleRenameSave = () => {
    if (!renamingItem || isInteractionDisabled) {
      return;
    }

    const nextName = renameDraft.trim();

    if (
      !nextName ||
      renamingItem.name === nextName ||
      !canRenameDocumentsPageItem(renamingItem)
    ) {
      resetRenameState();
      return;
    }

    if (renamingItem.kind === "folder") {
      patchFolderMutation.mutate({
        orgId,
        folderId: renamingItem.id,
        data: {
          name: nextName,
        },
      });
      resetRenameState();
      return;
    }

    patchDocumentMutation.mutate({
      orgId,
      docId: renamingItem.document.id,
      deviceId: renamingItem.document.deviceId ?? currentDeviceId ?? "",
      queryDeviceIds: renamingItem.document.deviceId
        ? [renamingItem.document.deviceId]
        : documentQueryDeviceIds,
      data: {
        title: nextName,
      },
    });
    resetRenameState();
  };

  const handleDeleteStart = (item: DocumentsPageItem) => {
    if (item.kind === "folder") {
      if (!isInteractionDisabled) setPendingFolderDeleteItem(item);
      return;
    }

    if (!isInteractionDisabled && canDeleteDocument(item.document)) {
      setPendingDocumentDeleteItem(item);
    }
  };

  const handleFolderDelete = () => {
    if (!pendingFolderDeleteItem || isInteractionDisabled) {
      return;
    }

    const folderId = pendingFolderDeleteItem.id;
    setPendingFolderDeleteItem(null);

    deleteFolderMutation.mutate({
      orgId,
      folderId,
    });
  };

  const handleDocumentDelete = () => {
    if (!pendingDocumentDeleteItem || isInteractionDisabled) {
      return;
    }

    const document = pendingDocumentDeleteItem.document;
    setPendingDocumentDeleteItem(null);

    void deleteDocument({
      document,
      navigateOnDelete: false,
    });
  };

  const getIsDisabled = (options?: { disabled?: boolean }) =>
    isInteractionDisabled || options?.disabled === true;

  const getRowActionProps = (
    item: DocumentsPageItem,
    options?: { disabled?: boolean }
  ): DocumentsTableRowActionsProps => {
    const canRenameItem = canRenameDocumentsPageItem(item);

    return {
      canDeleteItem: item.kind === "folder" || canDeleteDocument(item.document),
      canRenameItem,
      deleteTooltipTitle:
        item.kind === "document"
          ? getDeleteDocumentTooltipTitle(item.document)
          : undefined,
      disabled: getIsDisabled(options),
      item,
      onDeleteStart: handleDeleteStart,
      onMoveStart: setMoveItem,
      onOpenDocumentInNewTab,
      onOpen: onOpenItem,
      onRenameStart: handleRenameStart,
      onSetScope: (item) => {
        if (!getIsDisabled(options)) setScopeDocument(item.document);
      },
      renameTooltipTitle:
        item.kind === "document" && !canRenameItem
          ? LIVE_DOCUMENT_RENAME_TOOLTIP_TITLE
          : undefined,
    };
  };

  const handleMoveDialogConfirm = (target: DocumentsTableMoveTarget) => {
    if (!moveItem || isInteractionDisabled) {
      return;
    }

    const nextFolderId = target.type === "folder" ? target.folderId : null;

    if (moveItem.kind === "folder") {
      if (
        moveItem.folder.parentFolderId === nextFolderId ||
        (nextFolderId !== null &&
          isFolderInsideFolder({
            ancestorFolderId: moveItem.id,
            folderId: nextFolderId,
            foldersById,
          }))
      ) {
        return;
      }

      patchFolderMutation.mutate({
        orgId,
        folderId: moveItem.id,
        data: {
          parentFolderId: nextFolderId,
        },
      });
      return;
    }

    if (moveItem.document.folderId === nextFolderId) {
      return;
    }

    patchDocumentMutation.mutate({
      orgId,
      docId: moveItem.document.id,
      deviceId: moveItem.document.deviceId ?? currentDeviceId ?? "",
      queryDeviceIds: moveItem.document.deviceId
        ? [moveItem.document.deviceId]
        : documentQueryDeviceIds,
      data: {
        folderId: nextFolderId,
      },
    });
  };

  const renderActionsCell = (
    { row }: GridRenderCellParams<DocumentsTableRow, string>,
    options?: { disabled?: boolean }
  ) => {
    return (
      <DocumentsTableRowActions {...getRowActionProps(row.item, options)} />
    );
  };

  const openRowActionContextMenu = (
    item: DocumentsPageItem,
    anchorPosition: { top: number; left: number }
  ) => {
    if (isInteractionDisabled) {
      return;
    }

    setContextMenu({ anchorPosition, item });
  };

  const closeRowActionContextMenu = () => {
    setContextMenu(null);
  };

  const renderRowActionContextMenu = (options?: { disabled?: boolean }) => {
    if (!contextMenu) {
      return null;
    }

    return (
      <DocumentsTableRowActionsMenu
        {...getRowActionProps(contextMenu.item, options)}
        anchorPosition={contextMenu.anchorPosition}
        onClose={closeRowActionContextMenu}
      />
    );
  };
  const renameDialogTitle =
    renamingItem?.kind === "folder" ? "Rename Folder" : "Rename Document";
  const renderRowActionDialogs = (options?: { disabled?: boolean }) => {
    const disabled = getIsDisabled(options);
    const handleFolderDeleteConfirm = () => {
      if (disabled) {
        return;
      }

      handleFolderDelete();
    };
    const handleDocumentDeleteConfirm = () => {
      if (disabled) {
        return;
      }

      handleDocumentDelete();
    };

    return (
      <>
        {scopeDocument && (
          <SetDocumentScopeDialog
            document={scopeDocument}
            orgId={orgId}
            onClose={() => setScopeDocument(null)}
          />
        )}
        <Dialog
          aria-labelledby="documents-table-rename-dialog-title"
          fullWidth
          maxWidth="xs"
          onClose={resetRenameState}
          open={renamingItem !== null}
        >
          <DialogTitle id="documents-table-rename-dialog-title">
            {renameDialogTitle}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              margin="dense"
              onChange={(event) => setRenameDraft(event.target.value)}
              value={renameDraft}
            />
          </DialogContent>
          <DialogActions>
            <Button
              color="inherit"
              onClick={resetRenameState}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={disabled}
              onClick={handleRenameSave}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <DocumentsTableMoveDialog
          folders={folders}
          item={moveItem}
          onClose={() => setMoveItem(null)}
          onMove={(target) => {
            if (!disabled) {
              handleMoveDialogConfirm(target);
            }
          }}
        />

        <ConfirmationModal
          title="Delete folder"
          confirmText="Confirm"
          content="Deleting this folder will remove the folder itself and move any documents and child folders inside it to the root level."
          onAbort={() => setPendingFolderDeleteItem(null)}
          onConfirm={handleFolderDeleteConfirm}
          open={pendingFolderDeleteItem !== null}
        />

        <ConfirmationModal
          title="Delete document"
          confirmText="Confirm"
          content={<DocumentDeleteConfirmationContent />}
          onAbort={() => setPendingDocumentDeleteItem(null)}
          onConfirm={handleDocumentDeleteConfirm}
          open={pendingDocumentDeleteItem !== null}
        />
      </>
    );
  };

  return {
    isInteractionDisabled,
    openRowActionContextMenu,
    renderActionsCell,
    renderRowActionContextMenu,
    renderRowActionDialogs,
  };
};
